import { useQueryClient } from "@tanstack/react-query";
import Drawer from "@via/components/drawer.tsx";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { DrawerSize } from "@via/components/table/table.tsx";
import { PROCESS_CATALOG_QUERY_KEY } from "@via/hooks/useProcessCatalog.ts";
import {
  fetchProcessDetails,
  useDuplicateProcess,
  useOverwriteRevision,
  useSaveProcessRevision,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import CreateEditProcessForm from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/create-edit-process-form.tsx";
import {
  cleanAndParseProcess,
  packProcessToOverwrite,
  packProcessToSave,
  SaveProcessMode,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import { MenuGroupType } from "common";
import { useEffect, useRef, useState, type JSX } from "react";

// The form loads its menu group list by type, so a parsed process without one
// shows an empty Menu Group (#1547).
const parseProcess = (details: any): any => ({
  ...cleanAndParseProcess(details),
  type: MenuGroupType.PROCESS,
});

interface ProcessEditorDrawerProps {
  itemId: number | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  onOpenCopy: (copyId: number) => void;
}

export default function ProcessEditorDrawer({
  itemId,
  open,
  setOpen,
  onOpenCopy,
}: ProcessEditorDrawerProps): JSX.Element {
  const queryClient = useQueryClient();
  // Loaded data and the error are keyed by the item they belong to, so a
  // reopen on another item never renders the previous item's values.
  const [loaded, setLoaded] = useState<{ id: number; data: any } | null>(null);
  const [errorId, setErrorId] = useState<number | null>(null);

  const { mutate: createProcessRevision, isPending: isCreating } =
    useSaveProcessRevision();
  const { mutate: overwriteRevision, isPending: isOverwriting } =
    useOverwriteRevision();
  const { mutate: duplicateProcess, isPending: isDuplicating } =
    useDuplicateProcess();
  // What the drawer shows now, read when a duplicate returns: the closure that
  // started it may be stale.
  const shown = useRef({ open, itemId });
  shown.current = { open, itemId };

  const initialData =
    loaded !== null && loaded.id === itemId ? loaded.data : null;
  const hasError = errorId !== null && errorId === itemId;
  useEffect(() => {
    if (!open || itemId === null) return;
    let cancelled = false;
    setLoaded(null);
    setErrorId(null);
    fetchProcessDetails(String(itemId))
      .then(details => {
        if (!cancelled) setLoaded({ id: itemId, data: parseProcess(details) });
      })
      .catch(() => {
        if (!cancelled) setErrorId(itemId);
      });
    return () => {
      cancelled = true;
    };
  }, [open, itemId]);

  const refreshCatalog = (): void => {
    void queryClient.invalidateQueries({ queryKey: PROCESS_CATALOG_QUERY_KEY });
  };

  const handleChangeRevision = (revision: any): void => {
    if (itemId !== null)
      setLoaded({ id: itemId, data: parseProcess(revision) });
  };

  const handleSaveProcess = (processDraft: any): void => {
    createProcessRevision(
      {
        processId: processDraft?.processId,
        payload: packProcessToSave(processDraft),
      },
      {
        onSuccess: (revision: any) => {
          handleChangeRevision(revision);
          refreshCatalog();
        },
      }
    );
  };

  const handleOverwriteProcess = (
    processDraft: any,
    saveMode?: SaveProcessMode
  ): void => {
    overwriteRevision(
      {
        processId: processDraft?.processId,
        payload: packProcessToOverwrite(processDraft),
      },
      {
        onSuccess: (revision: any) => {
          handleChangeRevision(revision);
          refreshCatalog();
          if (saveMode !== SaveProcessMode.SAVE) setOpen(false);
        },
      }
    );
  };

  const handleDuplicateProcess = (processId: number): void => {
    if (isDuplicating) return;
    // The drawer stays open, so a failed duplicate leaves the original on screen.
    const from = itemId;
    duplicateProcess(processId, {
      onSuccess: (copy: { id: number }) => {
        refreshCatalog();
        // Only follow the copy if the user is still looking at the original.
        if (shown.current.open && shown.current.itemId === from)
          onOpenCopy(copy.id);
      },
    });
  };

  // Every close path refreshes the list, so a delete that ends with the form
  // closing itself still shows up.
  const closeDrawer = (): void => {
    refreshCatalog();
    setOpen(false);
  };

  const handleRevisionDeleted = (survivor: any): void => {
    handleChangeRevision(survivor);
    refreshCatalog();
  };

  const renderBody = (): JSX.Element => {
    if (hasError) {
      return (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      );
    }
    if (!initialData) {
      return (
        <div className="grid grid-cols-1 gap-6 p-4 py-6 sm:grid-cols-3 lg:grid-cols-4">
          <SkeletonLoader count={12} />
        </div>
      );
    }
    return (
      <CreateEditProcessForm
        title="Process"
        data={initialData}
        isCreateProcessRevisionLoading={isCreating}
        isOverwriteRevisionLoading={isOverwriting}
        isCreateNewProcessLoading={false}
        onSaveProcess={handleSaveProcess}
        onChangeRevision={handleChangeRevision}
        onOverwriteProcess={handleOverwriteProcess}
        onDuplicateProcess={handleDuplicateProcess}
        onRevisionDeleted={handleRevisionDeleted}
        onCancel={closeDrawer}
      />
    );
  };

  return (
    <Drawer
      id="process-catalog-edit"
      open={open}
      setOpen={closeDrawer}
      size={DrawerSize.Big}>
      {open ? renderBody() : <></>}
    </Drawer>
  );
}
